import { useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const beforeImageDef: SlotGroupDefinition = {
  label: 'Left Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a standout exterior or building photo that showcases the property architecture. Prefer polished building facades, street views, or community exteriors. Skip logos, floor plans, parking lots, utility areas, or low-value shots.',
    context:
      'Left side of a before/after neighborhood spotlight comparison — pair with a contrasting neighborhood or amenity scene on the right.',
  },
};

const afterImageDef: SlotGroupDefinition = {
  label: 'Right Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a standout neighborhood, outdoor, or amenity photo — parks, courtyards, street life, or surrounding area. Prefer lively, inviting outdoor scenes. Skip logos, floor plans, parking lots, utility areas, or low-value shots.',
    context:
      'Right side of a before/after neighborhood spotlight comparison — pair with the building exterior on the left.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore the Neighborhood' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function CompareSlider({
  beforeUrl,
  beforeAlt,
  afterUrl,
  afterAlt,
  beforeDef,
  afterDef,
}: {
  beforeUrl: string;
  beforeAlt: string;
  afterUrl: string;
  afterAlt: string;
  beforeDef: SlotGroupDefinition;
  afterDef: SlotGroupDefinition;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(50);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.max(5, Math.min(95, next)));
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      e.preventDefault();
      updateFromClientX(e.clientX);
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [updateFromClientX]);

  const startDrag = (e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    updateFromClientX(e.clientX);
  };

  return (
    <div
      ref={containerRef}
      className="neighborhoodspotlight-slider relative w-full aspect-[4/5] sm:aspect-[5/6] md:aspect-[4/5] lg:aspect-square overflow-hidden select-none touch-none"
      onPointerDown={startDrag}
      role="slider"
      aria-label="Compare images"
      aria-valuemin={5}
      aria-valuemax={95}
      aria-valuenow={Math.round(position)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') setPosition((p) => Math.max(5, p - 2));
        if (e.key === 'ArrowRight') setPosition((p) => Math.min(95, p + 2));
      }}
    >
      {/* Right / after image (full bleed) */}
      <EditableSlotGroup
        groupPath="neighborhoodspotlight.after_image"
        definition={afterDef}
        className="absolute inset-0"
      >
        {afterUrl ? (
          <VibesitesImage
            url={afterUrl}
            alt={afterAlt || 'Neighborhood view'}
            sizes="(min-width: 768px) 45vw, 100vw"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-muted" />
        )}
      </EditableSlotGroup>

      {/* Left / before image (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <EditableSlotGroup
          groupPath="neighborhoodspotlight.before_image"
          definition={beforeDef}
          className="absolute inset-0"
        >
          {beforeUrl ? (
            <VibesitesImage
              url={beforeUrl}
              alt={beforeAlt || 'Property exterior'}
              sizes="(min-width: 768px) 45vw, 100vw"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-muted" />
          )}
        </EditableSlotGroup>
      </div>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 z-10 pointer-events-none"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-white shadow-sm" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-md pointer-events-auto cursor-ew-resize">
          <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden="true">
            <path
              d="M5 1L1 6L5 11M13 1L17 6L13 11"
              stroke="var(--vibe-color-text-muted)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

export function NeighborhoodSpotlight() {
  const title = useContentToken('neighborhoodspotlight.title');
  const before = useSlotGroup('neighborhoodspotlight.before_image', beforeImageDef);
  const after = useSlotGroup('neighborhoodspotlight.after_image', afterImageDef);
  const cta = useSlotGroup('neighborhoodspotlight.cta', ctaDef);

  useRevealStyles();
  const mediaRef = useScrollReveal<HTMLDivElement>();
  const contentRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      className="w-full bg-background"
      aria-label="Neighborhood spotlight"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-6 py-16 sm:px-8 md:grid-cols-2 md:gap-12 md:px-10 md:py-20 lg:gap-16 lg:px-14 lg:py-24">
        <div ref={mediaRef} className="reveal-fade-up w-full">
          <CompareSlider
            beforeUrl={before.image || ''}
            beforeAlt={before.alt || ''}
            afterUrl={after.image || ''}
            afterAlt={after.alt || ''}
            beforeDef={beforeImageDef}
            afterDef={afterImageDef}
          />
        </div>

        <div
          ref={contentRef}
          className="reveal-fade-up flex flex-col items-center justify-center text-center px-2 sm:px-6 md:px-4 lg:px-10"
        >
          <EditableToken
            path="neighborhoodspotlight.title"
            as="h2"
            className="neighborhoodspotlight-title text-3xl sm:text-4xl md:text-[2.5rem] font-normal leading-tight text-primary mb-6"
          >
            {title || 'Expand Your Everyday'}
          </EditableToken>

          <RichTextContent
            path="neighborhoodspotlight.body"
            label="Body"
            defaultValue="<p>The neighborhood is in a central location to all downtown activities and events. From outdoor adventures to nightlife, you are in the middle of it all. You won't have to travel far when you live here!</p>"
            as="div"
            className="font-body text-base sm:text-[17px] text-foreground leading-[1.7] max-w-md mb-9 [&_p]:mb-0"
          />

          <EditableSlotGroup groupPath="neighborhoodspotlight.cta" definition={ctaDef}>
            <a
              href={cta.url || '/neighborhood'}
              target={cta._target || '_self'}
              className="neighborhoodspotlight-cta inline-flex items-center justify-center px-8 py-3.5 font-body text-[12px] sm:text-[13px] font-medium tracking-[0.18em] uppercase text-primary border border-primary bg-transparent transition-all duration-300 hover:bg-primary hover:text-primary-foreground"
            >
              {cta.label || 'Explore the Neighborhood'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .neighborhoodspotlight-title {
          font-family: Georgia, 'Times New Roman', serif;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodSpotlight',
  displayName: 'Neighborhood Spotlight',
  tokens: {
    'neighborhoodspotlight.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Expand Your Everyday',
    },
    'neighborhoodspotlight.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>The neighborhood is in a central location to all downtown activities and events. From outdoor adventures to nightlife, you are in the middle of it all. You won\'t have to travel far when you live here!</p>',
      suggestedFields: ['property_neighborhood_blurb'],
    },
  },
  slotGroups: {
    'neighborhoodspotlight.before_image': beforeImageDef,
    'neighborhoodspotlight.after_image': afterImageDef,
    'neighborhoodspotlight.cta': ctaDef,
  },
};

(NeighborhoodSpotlight as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodSpotlight;
